<template>
  <v-dialog 
    v-model="dialogModel" 
    content-class="haq-dialog"
    max-width="500px"
    persistent
    @click:outside="handleCancel"
  >
    <v-card class="haq-dialog-card save-query-modal">
      <v-card-title>
        <v-icon left>mdi-content-save</v-icon>
        Save Filters
        <v-spacer></v-spacer>
        <v-btn 
          icon 
          @click="handleCancel"
          :disabled="saving"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      
      <v-card-text>
        <v-form ref="form" v-model="formValid" lazy-validation>
          <HaqTextField
            v-model="queryName"
            label="Query Name"
            placeholder="Enter a name for your query..."
            :rules="nameRules"
            :counter="50"
            :disabled="saving"
            autofocus
            ref="queryName"
            @keyup.enter="handleSave"
          />
          
          <!-- Preview of current filters -->
          <div class="filter-preview mt-4">
            <h4 class="filter-preview-title">Current Filters:</h4>
            <div class="filter-preview-content">
              <v-chip
                v-if="filters.alarmStatus"
                small
                class="filter-chip"
              >
                <v-icon left small>mdi-alert</v-icon>
                Alarm: {{ filters.alarmStatus }}
              </v-chip>
              
              <v-chip
                v-if="filters.pointType"
                small
                class="filter-chip"
              >
                <v-icon left small>mdi-shape</v-icon>
                Type: {{ filters.pointType }}
              </v-chip>
              
              <v-chip
                v-if="filters.siteId && siteDisplayName"
                small
                class="filter-chip"
              >
                <v-icon left small>mdi-domain</v-icon>
                Site: {{ siteDisplayName }}
              </v-chip>
              
              <v-chip
                v-if="filters.zoneId && zoneDisplayName"
                small
                class="filter-chip"
              >
                <v-icon left small>mdi-map-marker</v-icon>
                Zone: {{ zoneDisplayName }}
              </v-chip>
              
              <v-chip
                v-if="filters.name"
                small
                class="filter-chip"
              >
                <v-icon left small>mdi-magnify</v-icon>
                Name: "{{ filters.name }}"
              </v-chip>
              
              <div v-if="!hasAnyFilters" class="no-filters-message">
                <v-icon color="grey">mdi-filter-off</v-icon>
                No filters applied
              </div>
            </div>
          </div>
        </v-form>
      </v-card-text>
      
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn 
          @click="handleCancel"
          :disabled="saving"
          class="haq-btn haq-btn-secondary"
        >
          Cancel
        </v-btn>
        <v-btn 
          @click="handleSave"
          :disabled="!formValid || !queryName || saving"
          :loading="saving"
          class="haq-btn haq-btn-primary"
        >
          Save Query
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { HTTPClient } from '@/assets/js/http';
import HaqTextField from '@/components/form/HaqTextField.vue';

let client = new HTTPClient();

export default {
  name: 'SaveQueryModal',
  components: {
    HaqTextField,
  },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    filters: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      queryName: '',
      formValid: false,
      saving: false,
      siteDisplayName: '',
      zoneDisplayName: '',
      
      nameRules: [
        v => !!v || 'Query name is required',
        v => (v && v.length <= 50) || 'Name must be less than 50 characters',
        v => (v && v.length >= 2) || 'Name must be at least 2 characters',
        v => (v && /^[a-zA-Z0-9\s\-_]+$/.test(v)) || 'Name can only contain letters, numbers, spaces, hyphens, and underscores'
      ]
    };
  },
  computed: {
    dialogModel: {
      get() {
        return this.dialog;
      },
      set(value) {
        this.$emit('update:dialog', value);
      }
    },
    
    hasAnyFilters() {
      return Object.values(this.filters).some(value => 
        value !== null && value !== undefined && value !== ''
      );
    }
  },
  methods: {
    async handleSave() {
      if (!this.$refs.form.validate()) {
        return;
      }
      
      if (!this.hasAnyFilters) {
        window.M.toast({ 
          html: 'Cannot save query with no filters applied' 
        });
        return;
      }
      
      try {
        this.saving = true;
        
        // Check if query name already exists
        const existingQueries = await client.robustQuery('/api/querypoints/queries');
        const duplicateQuery = existingQueries.results.find(
          query => query.name.toLowerCase() === this.queryName.toLowerCase()
        );
        
        if (duplicateQuery) {
          window.M.toast({ 
            html: 'A query with this name already exists. Please choose a different name.' 
          });
          this.saving = false;
          return;
        }
        
        // Emit save event to parent
        this.$emit('save-query', {
          name: this.queryName,
          filters: { ...this.filters }
        });
        
        // Reset form and close modal
        this.resetForm();
        this.dialogModel = false;
        
      } catch (error) {
        console.error('Error checking existing queries:', error);
        window.M.toast({ 
          html: 'Error saving query. Please try again.' 
        });
      } finally {
        this.saving = false;
      }
    },
    
    handleCancel() {
      this.resetForm();
      this.dialogModel = false;
    },
    
    resetForm() {
      this.queryName = '';
      this.formValid = false;
      this.saving = false;
      this.siteDisplayName = '';
      this.zoneDisplayName = '';
      
      // Reset form validation
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    
    async loadDisplayNames() {
      try {
        // Load site display name
        if (this.filters.siteId) {
          const sites = this.$store.state.sites || [];
          const site = sites.find(s => s.objectId === this.filters.siteId);
          this.siteDisplayName = site ? site.name : 'Unknown Site';
        }
        
        // Load zone display name
        if (this.filters.zoneId) {
          const result = await client.robustQuery('/api/zones', { 
            siteId: this.filters.siteId 
          });
          const zone = result.results.find(z => z.objectId === this.filters.zoneId);
          this.zoneDisplayName = zone ? zone.name : 'Unknown Zone';
        }
        
      } catch (error) {
        console.error('Error loading display names:', error);
      }
    }
  },
  watch: {
    dialog(newValue) {
      if (newValue) {
        // Modal is opening
        this.resetForm();
        this.loadDisplayNames();
        
        // Use $nextTick to ensure DOM is updated before accessing refs
        this.$nextTick(() => {
          console.log("refs", this.$refs);
          console.log("queryName", this.$refs.queryName);
          this.$refs.queryName?.$el.querySelector("input")?.classList.add("browser-default");
        });
      }
    }
  }
};
</script>

<style lang="sass" scoped>
.filter-preview
  background: var(--app-secondary-bg)
  border-radius: 8px
  padding: 0px 16px 16px 16px
  border: 1px solid var(--border)
  margin-top: 0px !important

  .filter-preview-title
    color: var(--app-primary-txt)
    font-size: 14px
    font-weight: 500

  .filter-preview-content
    display: flex
    flex-wrap: wrap
    gap: 8px

    .filter-chip
      margin: 0
      background: var(--app-primary-bg)

      .v-icon
        margin-right: 4px

    .no-filters-message
      display: flex
      align-items: center
      gap: 8px
      color: var(--app-tertiary-txt)
      font-style: italic

      .v-icon
        font-size: 18px

@media (max-width: 600px)
  .filter-preview
    padding: 12px
    
    .filter-preview-content
      .filter-chip
        font-size: 12px
        height: 24px
        background: var(--app-primary-bg)
</style> 